/* eslint-disable class-methods-use-this */
import ReduceStore from "flux/lib/FluxReduceStore";

import ProjectAIOrganizeResult, {
    ProjectAIOrganizeResultStatus,
    ProjectAIOrganizeResultSuggestion
} from "../../../foundation/api/model/projectAIOrganize/ProjectAIOrganizeResult";

import AppActionTypes from "../app/AppActionTypes";
import BarrelActionTypes from "../barrel/BarrelActionTypes";
import { AllPayloads } from "../payloads";

import * as Payloads from "./ProjectAIOrganizeActionPayloads";
import ProjectAIOrganizeActionTypes from "./ProjectAIOrganizeActionTypes";
import {
    ProjectAIOrganizeResultRecord,
    ProjectAIOrganizeRejectedSectionIdsRecord,
    ProjectAIOrganizeUserCreatedSectionIdsRecord
} from "./types";

interface State {
    isGetProjectAIOrganizeResultPending: boolean;
    isCreateProjectAIOrganizePending: boolean;
    isApplyProjectAIOrganizePending: boolean;
    isCancelProjectAIOrganizePending: boolean;
    projectAIOrganizeResultRecord: ProjectAIOrganizeResultRecord;
    projectAIOrganizeRejectedSectionIdsRecord: ProjectAIOrganizeRejectedSectionIdsRecord;
    projectAIOrganizeUserCreatedSectionIdsRecord: ProjectAIOrganizeUserCreatedSectionIdsRecord;
}

class ProjectAIOrganizeStore extends ReduceStore<State, AllPayloads> {
    getInitialState(): State {
        return {
            isGetProjectAIOrganizeResultPending: true,
            isCreateProjectAIOrganizePending: false,
            isApplyProjectAIOrganizePending: false,
            isCancelProjectAIOrganizePending: false,
            projectAIOrganizeResultRecord: {},
            projectAIOrganizeRejectedSectionIdsRecord: {},
            projectAIOrganizeUserCreatedSectionIdsRecord: {}
        };
    }

    #getProjectAIOrganizeResult(state: State, pid: string): ProjectAIOrganizeResult | undefined {
        return state.projectAIOrganizeResultRecord[pid];
    }

    #getProjectAIOrganizeRejectedSectionIds(state: State, pid: string): string[] | undefined {
        return state.projectAIOrganizeRejectedSectionIdsRecord[pid];
    }

    #getProjectAIOrganizeUserCreatedSectionIds(state: State, pid: string): string[] | undefined {
        return state.projectAIOrganizeUserCreatedSectionIdsRecord[pid];
    }

    reset(state: State): State {
        return {
            ...this.getInitialState(),
            projectAIOrganizeResultRecord: state.projectAIOrganizeResultRecord
        };
    }

    getProjectAIOrganizeResultSuccess(state: State, {
        pid,
        projectAIOrganizeResult
    }: Payloads.GetProjectAIOrganizeResultSuccess): State {
        return {
            ...state,
            isGetProjectAIOrganizeResultPending: false,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: projectAIOrganizeResult
            }
        };
    }

    getProjectAIOrganizeResultFinish(state: State): State {
        return {
            ...state,
            isGetProjectAIOrganizeResultPending: false
        };
    }

    updateProjectAIOrganizeResult(state: State, {
        pid,
        payload
    }: Payloads.UpdateProjectAIOrganizeResult): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        return {
            ...state,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: {
                    ...(projectAIOrganizeResult || {}),
                    ...payload
                } as ProjectAIOrganizeResult
            }
        };
    }

    acceptProjectAIOrganizeSection(state: State, {
        pid,
        sectionId
    }: Payloads.AcceptProjectAIOrganizeSection): State {
        const projectAIOrganizeRejectedSectionIds = this.#getProjectAIOrganizeRejectedSectionIds(state, pid);

        return {
            ...state,
            projectAIOrganizeRejectedSectionIdsRecord: {
                ...state.projectAIOrganizeRejectedSectionIdsRecord,
                [pid]: (projectAIOrganizeRejectedSectionIds || [])
                    .filter(rejectedSectionId => rejectedSectionId !== sectionId)
            }
        };
    }

    rejectProjectAIOrganizeSection(state: State, {
        pid,
        sectionId
    }: Payloads.RejectProjectAIOrganizeSection): State {
        const projectAIOrganizeRejectedSectionIds = this.#getProjectAIOrganizeRejectedSectionIds(state, pid);

        return {
            ...state,
            projectAIOrganizeRejectedSectionIdsRecord: {
                ...state.projectAIOrganizeRejectedSectionIdsRecord,
                [pid]: [...(projectAIOrganizeRejectedSectionIds || []), sectionId]
            }
        };
    }

    createProjectAIOrganize(state: State, {
        pid,
        screens,
        variantGroups
    }: Payloads.CreateProjectAIOrganize): State {
        return {
            ...state,
            isCreateProjectAIOrganizePending: true,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: {
                    status: ProjectAIOrganizeResultStatus.PENDING,
                    requestedScreens: screens,
                    requestedVariantGroups: variantGroups
                }
            }
        };
    }

    createProjectAIOrganizeSuccess(state: State, {
        pid,
        projectAIOrganizeResultPatch
    }: Payloads.CreateProjectAIOrganizeSuccess): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        return {
            ...state,
            isCreateProjectAIOrganizePending: false,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: projectAIOrganizeResult ? {
                    ...projectAIOrganizeResult,
                    ...projectAIOrganizeResultPatch
                } : undefined
            }
        };
    }

    createProjectAIOrganizeFailure(state: State, {
        pid,
        isRetry
    }: Payloads.CreateProjectAIOrganizeFailure): State {
        const computedProjectAIOrganizeResultRecord = { ...state.projectAIOrganizeResultRecord };
        const computedProjectAIOrganizeRejectedSectionIdsRecord = {
            ...state.projectAIOrganizeRejectedSectionIdsRecord
        };
        const computedProjectAIOrganizeUserCreatedSectionIdsRecord = {
            ...state.projectAIOrganizeUserCreatedSectionIdsRecord
        };
        let projectAIOrganizeResultRecord;
        let projectAIOrganizeRejectedSectionIdsRecord = computedProjectAIOrganizeRejectedSectionIdsRecord;
        let projectAIOrganizeUserCreatedSectionIdsRecord = computedProjectAIOrganizeUserCreatedSectionIdsRecord;

        if (isRetry) {
            const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

            projectAIOrganizeResultRecord = {
                ...computedProjectAIOrganizeResultRecord,
                [pid]: projectAIOrganizeResult ? {
                    ...projectAIOrganizeResult,
                    status: ProjectAIOrganizeResultStatus.FAILED
                } : undefined
            };
        } else {
            delete computedProjectAIOrganizeResultRecord[pid];
            delete computedProjectAIOrganizeRejectedSectionIdsRecord[pid];
            delete computedProjectAIOrganizeUserCreatedSectionIdsRecord[pid];

            projectAIOrganizeResultRecord = computedProjectAIOrganizeResultRecord;
            projectAIOrganizeRejectedSectionIdsRecord = computedProjectAIOrganizeRejectedSectionIdsRecord;
            projectAIOrganizeUserCreatedSectionIdsRecord = computedProjectAIOrganizeUserCreatedSectionIdsRecord;
        }

        return {
            ...state,
            isCreateProjectAIOrganizePending: false,
            projectAIOrganizeResultRecord,
            projectAIOrganizeRejectedSectionIdsRecord,
            projectAIOrganizeUserCreatedSectionIdsRecord
        };
    }

    applyProjectAIOrganize(state: State): State {
        return {
            ...state,
            isApplyProjectAIOrganizePending: true
        };
    }

    applyProjectAIOrganizeSuccess(state: State, { pid }: Payloads.ApplyProjectAIOrganizeSuccess): State {
        const computedProjectAIOrganizeResultRecord = { ...state.projectAIOrganizeResultRecord };
        const computedProjectAIOrganizeRejectedSectionIdsRecord = {
            ...state.projectAIOrganizeRejectedSectionIdsRecord
        };
        const computedProjectAIOrganizeUserCreatedSectionIdsRecord = {
            ...state.projectAIOrganizeUserCreatedSectionIdsRecord
        };

        delete computedProjectAIOrganizeResultRecord[pid];
        delete computedProjectAIOrganizeRejectedSectionIdsRecord[pid];
        delete computedProjectAIOrganizeUserCreatedSectionIdsRecord[pid];

        return {
            ...state,
            isApplyProjectAIOrganizePending: false,
            projectAIOrganizeResultRecord: computedProjectAIOrganizeResultRecord,
            projectAIOrganizeRejectedSectionIdsRecord: computedProjectAIOrganizeRejectedSectionIdsRecord,
            projectAIOrganizeUserCreatedSectionIdsRecord: computedProjectAIOrganizeUserCreatedSectionIdsRecord
        };
    }

    applyProjectAIOrganizeFailure(state: State): State {
        return {
            ...state,
            isApplyProjectAIOrganizePending: false
        };
    }

    cancelProjectAIOrganize(state: State): State {
        return {
            ...state,
            isCancelProjectAIOrganizePending: true
        };
    }

    cancelProjectAIOrganizeSuccess(state: State, { pid }: Payloads.CancelProjectAIOrganizeSuccess): State {
        const computedProjectAIOrganizeResultRecord = { ...state.projectAIOrganizeResultRecord };
        const computedProjectAIOrganizeRejectedSectionIdsRecord = {
            ...state.projectAIOrganizeRejectedSectionIdsRecord
        };
        const computedProjectAIOrganizeUserCreatedSectionIdsRecord = {
            ...state.projectAIOrganizeUserCreatedSectionIdsRecord
        };

        delete computedProjectAIOrganizeResultRecord[pid];
        delete computedProjectAIOrganizeRejectedSectionIdsRecord[pid];
        delete computedProjectAIOrganizeUserCreatedSectionIdsRecord[pid];

        return {
            ...state,
            isCancelProjectAIOrganizePending: false,
            projectAIOrganizeResultRecord: computedProjectAIOrganizeResultRecord,
            projectAIOrganizeRejectedSectionIdsRecord: computedProjectAIOrganizeRejectedSectionIdsRecord,
            projectAIOrganizeUserCreatedSectionIdsRecord: computedProjectAIOrganizeUserCreatedSectionIdsRecord
        };
    }

    cancelProjectAIOrganizeFailure(state: State): State {
        return {
            ...state,
            isCancelProjectAIOrganizePending: false
        };
    }

    updateProjectAIOrganizeSection(state: State, {
        pid,
        sectionId,
        isUserCreatedSection,
        items,
        payload
    }: Payloads.UpdateProjectAIOrganizeSection): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        if (!projectAIOrganizeResult) {
            return state;
        }

        const suggestions = projectAIOrganizeResult.suggestions ?? {} as ProjectAIOrganizeResultSuggestion;
        const computedNewSections = [...(suggestions.newSections || [])];
        const computedUpdatedSections = [...(suggestions.updatedSections || [])];

        let sectionIndex = computedNewSections.findIndex(s => s.id === sectionId);

        if (sectionIndex >= 0) {
            computedNewSections[sectionIndex] = {
                ...computedNewSections[sectionIndex],
                ...payload
            };
        } else {
            sectionIndex = computedUpdatedSections.findIndex(s => s.id === sectionId);

            if (sectionIndex < 0) {
                computedUpdatedSections.push({
                    id: sectionId,
                    items,
                    ...payload
                });
            } else {
                computedUpdatedSections[sectionIndex] = {
                    ...computedUpdatedSections[sectionIndex],
                    ...payload
                };
            }
        }

        const computedProjectAIOrganizeResult = {
            ...projectAIOrganizeResult,
            suggestions: {
                ...suggestions,
                newSections: computedNewSections,
                updatedSections: computedUpdatedSections
            }
        };

        const projectAIOrganizeUserCreatedSectionIds = [
            ...(this.#getProjectAIOrganizeUserCreatedSectionIds(state, pid) || [])
        ];

        if (isUserCreatedSection && payload.id) {
            projectAIOrganizeUserCreatedSectionIds.push(payload.id);
        }

        return {
            ...state,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: computedProjectAIOrganizeResult
            },
            projectAIOrganizeUserCreatedSectionIdsRecord: {
                ...state.projectAIOrganizeUserCreatedSectionIdsRecord,
                [pid]: projectAIOrganizeUserCreatedSectionIds
            }
        };
    }

    updateProjectAIOrganizeSectionOrder(state: State, {
        pid,
        payload
    }: Payloads.UpdateProjectAIOrganizeSectionOrder): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        if (!projectAIOrganizeResult) {
            return state;
        }

        const suggestions = projectAIOrganizeResult.suggestions ?? {} as ProjectAIOrganizeResultSuggestion;
        const computedNewSections = [...(suggestions.newSections || [])];
        const computedUpdatedSections = [...(suggestions.updatedSections || [])];

        for (const sectionToUpdate of payload) {
            let sectionIndex = computedNewSections.findIndex(s => s.id === sectionToUpdate.id);

            if (sectionIndex >= 0) {
                computedNewSections[sectionIndex] = {
                    ...computedNewSections[sectionIndex],
                    order: sectionToUpdate.order,
                    parent: sectionToUpdate.parent
                };

                continue;
            }

            sectionIndex = computedUpdatedSections.findIndex(s => s.id === sectionToUpdate.id);

            if (sectionIndex < 0) {
                computedUpdatedSections.push({
                    id: sectionToUpdate.id!,
                    order: sectionToUpdate.order,
                    parent: sectionToUpdate.parent,
                    items: sectionToUpdate.items
                });
            } else {
                computedUpdatedSections[sectionIndex] = {
                    ...computedUpdatedSections[sectionIndex],
                    order: sectionToUpdate.order,
                    parent: sectionToUpdate.parent
                };
            }
        }

        const computedProjectAIOrganizeResult = {
            ...projectAIOrganizeResult,
            suggestions: {
                ...suggestions,
                newSections: computedNewSections,
                updatedSections: computedUpdatedSections
            }
        };

        return {
            ...state,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: computedProjectAIOrganizeResult
            }
        };
    }

    updateProjectAIOrganizeSectionScreenOrder(state: State, {
        pid,
        items,
        targetSectionItems,
        sourceSections,
        targetSection,
        targetIndex
    }: Payloads.UpdateProjectAIOrganizeSectionScreenOrder): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        if (!projectAIOrganizeResult) {
            return state;
        }

        const suggestions = projectAIOrganizeResult.suggestions ?? {} as ProjectAIOrganizeResultSuggestion;
        const computedNewSections = [...(suggestions.newSections || [])];
        const computedUpdatedSections = [...(suggestions.updatedSections || [])];

        let targetSectionIndex = computedNewSections.findIndex(s => s.id === targetSection._id);

        if (targetSectionIndex < 0) {
            targetSectionIndex = computedUpdatedSections.findIndex(s => s.id === targetSection._id);

            if (targetSectionIndex < 0) {
                computedUpdatedSections.push({
                    id: targetSection._id,
                    order: targetSection.order,
                    parent: targetSection.parent,
                    items: targetSectionItems
                });
            }
        }

        for (const sourceSection of sourceSections) {
            let sourceSectionIndex = computedNewSections.findIndex(s => s.id === sourceSection.id);

            if (sourceSectionIndex < 0) {
                sourceSectionIndex = computedUpdatedSections.findIndex(s => s.id === sourceSection.id);

                if (sourceSectionIndex < 0 && sourceSection.id !== targetSection._id) {
                    computedUpdatedSections.push(sourceSection);
                }
            }
        }

        const sectionItemIds = new Set((items || []).map(item => item.id));

        computedNewSections.forEach(newSection => {
            newSection.items = newSection.items?.filter(item => !sectionItemIds.has(item.id));
        });

        computedUpdatedSections.forEach(updatedSection => {
            updatedSection.items = updatedSection.items?.filter(item => !sectionItemIds.has(item.id));
        });

        targetSectionIndex = computedNewSections.findIndex(s => s.id === targetSection._id);

        if (targetSectionIndex >= 0) {
            computedNewSections[targetSectionIndex].items?.splice(targetIndex, 0, ...items);
        } else {
            targetSectionIndex = computedUpdatedSections.findIndex(s => s.id === targetSection._id);

            if (targetSectionIndex >= 0) {
                computedUpdatedSections[targetSectionIndex].items?.splice(targetIndex, 0, ...items);
            }
        }

        const computedProjectAIOrganizeResult = {
            ...projectAIOrganizeResult,
            suggestions: {
                ...suggestions,
                newSections: computedNewSections,
                updatedSections: computedUpdatedSections
            }
        };

        return {
            ...state,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: computedProjectAIOrganizeResult
            }
        };
    }

    addProjectAIOrganizeSection(state: State, {
        pid,
        section
    }: Payloads.AddProjectAIOrganizeSection): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        if (!projectAIOrganizeResult) {
            return state;
        }

        const suggestions = projectAIOrganizeResult.suggestions
            ? { ...projectAIOrganizeResult.suggestions }
            : {} as ProjectAIOrganizeResultSuggestion;
        const sectionItemIds = new Set((section.items || []).map(item => item.id));

        suggestions.newSections.forEach(newSection => {
            newSection.items = newSection.items?.filter(item => !sectionItemIds.has(item.id));
        });

        suggestions.updatedSections.forEach(updatedSection => {
            updatedSection.items = updatedSection.items?.filter(item => !sectionItemIds.has(item.id));
        });

        suggestions.newSections.push(section);

        return {
            ...state,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: {
                    ...projectAIOrganizeResult,
                    suggestions
                }
            }
        };
    }

    projectAIOrganizeSetAsVariant(state: State, {
        pid,
        variantGroupId,
        isNewVariant,
        name,
        screens,
        items,
        targetSectionItems,
        sourceSections,
        targetSection,
        targetIndex
    }: Payloads.ProjectAIOrganizeSetAsVariant): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        if (!projectAIOrganizeResult) {
            return state;
        }

        const suggestions = projectAIOrganizeResult.suggestions ?? {} as ProjectAIOrganizeResultSuggestion;
        const computedNewSections = [...(suggestions.newSections || [])];
        const computedUpdatedSections = [...(suggestions.updatedSections || [])];
        const computedNewVariantGroups = [...(suggestions.newVariantGroups || [])];
        const computedUpdatedVariantGroups = [...(suggestions.updatedVariantGroups || [])];

        let targetSectionIndex = computedNewSections.findIndex(s => s.id === targetSection._id);

        if (targetSectionIndex < 0) {
            targetSectionIndex = computedUpdatedSections.findIndex(s => s.id === targetSection._id);

            if (targetSectionIndex < 0) {
                computedUpdatedSections.push({
                    id: targetSection._id,
                    order: targetSection.order,
                    parent: targetSection.parent,
                    items: targetSectionItems
                });
            }
        }

        for (const sourceSection of sourceSections) {
            let sourceSectionIndex = computedNewSections.findIndex(s => s.id === sourceSection.id);

            if (sourceSectionIndex < 0) {
                sourceSectionIndex = computedUpdatedSections.findIndex(s => s.id === sourceSection.id);

                if (sourceSectionIndex < 0 && sourceSection.id !== targetSection._id) {
                    computedUpdatedSections.push(sourceSection);
                }
            }
        }

        const sectionItemIds = new Set((items || []).map(item => item.id));

        computedNewSections.forEach(newSection => {
            newSection.items = newSection.items?.filter(item => !sectionItemIds.has(item.id));
        });

        computedUpdatedSections.forEach(updatedSection => {
            updatedSection.items = updatedSection.items?.filter(item => !sectionItemIds.has(item.id));
        });

        targetSectionIndex = computedNewSections.findIndex(s => s.id === targetSection._id);

        if (targetSectionIndex >= 0) {
            computedNewSections[targetSectionIndex].items?.splice(targetIndex, 0, { id: variantGroupId, type: "variantGroup" });
        } else {
            targetSectionIndex = computedUpdatedSections.findIndex(s => s.id === targetSection._id);

            if (targetSectionIndex >= 0) {
                computedUpdatedSections[targetSectionIndex].items?.splice(targetIndex, 0, { id: variantGroupId, type: "variantGroup" });
            }
        }

        if (isNewVariant) {
            computedNewVariantGroups.push({
                id: variantGroupId,
                name,
                screens
            });
        } else {
            let variantGroupIndex = computedNewVariantGroups.findIndex(v => v.id === variantGroupId);

            if (variantGroupIndex >= 0) {
                computedNewVariantGroups[variantGroupIndex].screens.push(...screens);
            } else {
                variantGroupIndex = computedUpdatedVariantGroups.findIndex(v => v.id === variantGroupId);

                if (variantGroupIndex >= 0) {
                    computedUpdatedVariantGroups[variantGroupIndex].screens.push(...screens);
                } else {
                    computedUpdatedVariantGroups.push({
                        id: variantGroupId,
                        screens
                    });
                }
            }
        }

        const computedProjectAIOrganizeResult = {
            ...projectAIOrganizeResult,
            suggestions: {
                ...suggestions,
                newSections: computedNewSections,
                updatedSections: computedUpdatedSections,
                newVariantGroups: computedNewVariantGroups,
                updatedVariantGroups: computedUpdatedVariantGroups
            }
        };

        return {
            ...state,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: computedProjectAIOrganizeResult
            }
        };
    }

    deleteProjectAIOrganizeSection(state: State, {
        pid,
        items,
        targetSectionItems,
        targetSection,
        sectionIdsToDelete
    }: Payloads.DeleteProjectAIOrganizeSection): State {
        const projectAIOrganizeResult = this.#getProjectAIOrganizeResult(state, pid);

        if (!projectAIOrganizeResult) {
            return state;
        }

        const suggestions = projectAIOrganizeResult.suggestions ?? {} as ProjectAIOrganizeResultSuggestion;
        let computedNewSections = [...(suggestions.newSections || [])];
        const computedUpdatedSections = [...(suggestions.updatedSections || [])];

        console.log("deleteProjectAIOrganizeSection", suggestions, targetSection, targetSectionItems);

        computedNewSections = computedNewSections.filter(newSection => !sectionIdsToDelete.includes(newSection.id));

        let targetSectionIndex = computedNewSections.findIndex(s => s.id === targetSection._id);

        if (targetSectionIndex >= 0) {
            computedNewSections[targetSectionIndex].items?.push(...items);
        } else {
            targetSectionIndex = computedUpdatedSections.findIndex(s => s.id === targetSection._id);

            if (targetSectionIndex >= 0) {
                computedUpdatedSections[targetSectionIndex].items?.push(...items);
            } else {
                computedUpdatedSections.push({
                    id: targetSection._id,
                    order: targetSection.order,
                    parent: targetSection.parent,
                    items: [...targetSectionItems, ...items]
                });
            }
        }

        const computedProjectAIOrganizeResult = {
            ...projectAIOrganizeResult,
            suggestions: {
                ...suggestions,
                newSections: computedNewSections,
                updatedSections: computedUpdatedSections
            }
        };

        console.log(computedProjectAIOrganizeResult);
        return {
            ...state,
            projectAIOrganizeResultRecord: {
                ...state.projectAIOrganizeResultRecord,
                [pid]: computedProjectAIOrganizeResult
            }
        };
    }

    // eslint-disable-next-line complexity
    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            case AppActionTypes.RESET:
            case BarrelActionTypes.RESET:
                return this.reset(state);

            case ProjectAIOrganizeActionTypes.GET_PROJECT_AI_ORGANIZE_RESULT_SUCCESS:
                return this.getProjectAIOrganizeResultSuccess(state, action);
            case ProjectAIOrganizeActionTypes.GET_PROJECT_AI_ORGANIZE_RESULT_FINISH:
            case ProjectAIOrganizeActionTypes.GET_PROJECT_AI_ORGANIZE_RESULT_FAILURE:
                return this.getProjectAIOrganizeResultFinish(state);

            case ProjectAIOrganizeActionTypes.UPDATE_PROJECT_AI_ORGANIZE_RESULT:
                return this.updateProjectAIOrganizeResult(state, action);

            case ProjectAIOrganizeActionTypes.ACCEPT_PROJECT_AI_ORGANIZE_SECTION:
                return this.acceptProjectAIOrganizeSection(state, action);
            case ProjectAIOrganizeActionTypes.REJECT_PROJECT_AI_ORGANIZE_SECTION:
                return this.rejectProjectAIOrganizeSection(state, action);

            case ProjectAIOrganizeActionTypes.CREATE_PROJECT_AI_ORGANIZE:
                return this.createProjectAIOrganize(state, action);
            case ProjectAIOrganizeActionTypes.CREATE_PROJECT_AI_ORGANIZE_SUCCESS:
                return this.createProjectAIOrganizeSuccess(state, action);
            case ProjectAIOrganizeActionTypes.CREATE_PROJECT_AI_ORGANIZE_FAILURE:
                return this.createProjectAIOrganizeFailure(state, action);

            case ProjectAIOrganizeActionTypes.APPLY_PROJECT_AI_ORGANIZE:
                return this.applyProjectAIOrganize(state);
            case ProjectAIOrganizeActionTypes.APPLY_PROJECT_AI_ORGANIZE_SUCCESS:
                return this.applyProjectAIOrganizeSuccess(state, action);
            case ProjectAIOrganizeActionTypes.APPLY_PROJECT_AI_ORGANIZE_FAILURE:
                return this.applyProjectAIOrganizeFailure(state);

            case ProjectAIOrganizeActionTypes.CANCEL_PROJECT_AI_ORGANIZE:
                return this.cancelProjectAIOrganize(state);
            case ProjectAIOrganizeActionTypes.CANCEL_PROJECT_AI_ORGANIZE_SUCCESS:
                return this.cancelProjectAIOrganizeSuccess(state, action);
            case ProjectAIOrganizeActionTypes.CANCEL_PROJECT_AI_ORGANIZE_FAILURE:
                return this.cancelProjectAIOrganizeFailure(state);

            case ProjectAIOrganizeActionTypes.ADD_PROJECT_AI_ORGANIZE_SECTION:
                return this.addProjectAIOrganizeSection(state, action);

            case ProjectAIOrganizeActionTypes.UPDATE_PROJECT_AI_ORGANIZE_SECTION:
                return this.updateProjectAIOrganizeSection(state, action);

            case ProjectAIOrganizeActionTypes.UPDATE_PROJECT_AI_ORGANIZE_SECTION_ORDER:
                return this.updateProjectAIOrganizeSectionOrder(state, action);

            case ProjectAIOrganizeActionTypes.UPDATE_PROJECT_AI_ORGANIZE_SECTION_SCREEN_ORDER:
                return this.updateProjectAIOrganizeSectionScreenOrder(state, action);

            case ProjectAIOrganizeActionTypes.PROJECT_AI_ORGANIZE_SET_AS_VARIANT:
                return this.projectAIOrganizeSetAsVariant(state, action);

            case ProjectAIOrganizeActionTypes.DELETE_PROJECT_AI_ORGANIZE_SECTION:
                return this.deleteProjectAIOrganizeSection(state, action);

            default:
                return state;
        }
    }
}

export default ProjectAIOrganizeStore;
export {
    State as ProjectAIOrganizeStoreState
};
